<template>
    <div v-if="editor">
        <v-slide-group show-arrows>
            <v-slide-item>
                <span class="app-wysiwyg__group">
                    <app-wysiwyg-toolbar-type-menu
                        :types="activeTypes"
                        :active="node"
                        :disabled="disabled"
                        @run="run($event)"
                    />
                    <app-wysiwyg-toolbar-field-menu
                        v-if="form"
                        :fields="activeFields"
                        :active="node"
                        :disabled="disabled"
                        @run="run($event)"
                    />
                </span>
            </v-slide-item>

            <v-slide-item>
                <span class="app-wysiwyg__group">
                    <app-wysiwyg-toolbar-mark
                        v-for="(mark, markKey) in activeMarks"
                        :key="markKey"
                        :editor="editor"
                        :name="markKey"
                        :icon="mark.icon"
                        :text="mark.text"
                        :shortcut="mark.shortcut"
                        :options="mark.options"
                        :disabled="disabled || !can(markKey)"
                        :active="editor.isActive(markKey)"
                        @run="$event => run(mark.command, $event)"
                    />
                </span>
            </v-slide-item>

            <v-slide-item>
                <span class="app-wysiwyg__group">
                    <app-wysiwyg-toolbar-action
                        :editor="editor"
                        name="DynamicValue"
                        icon="dynamicValue"
                        text="Dynamic Value"
                        :disabled="disabled || !can('DynamicValue')"
                        @run="run(ctx => ctx.editor.chain().focus().insertDynamicValue().run())"
                    />
                </span>
            </v-slide-item>

            <v-slide-item>
                <span class="app-wysiwyg__group">
                    <app-wysiwyg-toolbar-action
                        v-for="(action, actionKey) in activeActions"
                        :key="actionKey"
                        :editor="editor"
                        :name="actionKey"
                        :icon="action.icon"
                        :text="action.text"
                        :disabled="disabled || !can(actionKey)"
                        @run="run(action.command)"
                    />
                </span>
            </v-slide-item>
        </v-slide-group>
    </div>
</template>

<script>
import { typeSchema, fieldSchema, markSchema, actionSchema, findActiveNode } from '@/lib/tiptap';

export default {
    name: 'AppWysiwygToolbar',
    components: {
        AppWysiwygToolbarTypeMenu: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppWysiwygToolbarTypeMenu'
            ),
        AppWysiwygToolbarFieldMenu: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppWysiwygToolbarFieldMenu'
            ),
        AppWysiwygToolbarMark: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppWysiwygToolbarMark'
            ),
        AppWysiwygToolbarAction: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppWysiwygToolbarAction'
            )
    },
    props: {
        editor: {
            type: Object,
            required: true
        },
        form: {
            type: Boolean,
            default: false
        },
        exclude: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        typeSchema,
        fieldSchema,
        markSchema,
        actionSchema
    }),
    computed: {
        activeTypes() {
            if (this.exclude.length === 0) {
                return this.typeSchema;
            }

            const includedTypes = {};

            for (const key in this.typeSchema) {
                if (!this.exclude.includes(key)) {
                    includedTypes[`${key}`] = this.typeSchema[`${key}`];
                }
            }

            return includedTypes;
        },
        activeFields() {
            if (this.exclude.length === 0) {
                return this.fieldSchema;
            }

            const includedFields = {};

            for (const key in this.fieldSchema) {
                if (!this.exclude.includes(key)) {
                    includedFields[`${key}`] = this.fieldSchema[`${key}`];
                }
            }

            return includedFields;
        },
        activeMarks() {
            if (this.exclude.length === 0) {
                return this.markSchema;
            }

            const includedMarks = {};

            for (const key in this.markSchema) {
                if (!this.exclude.includes(key)) {
                    includedMarks[`${key}`] = this.markSchema[`${key}`];
                }
            }

            return includedMarks;
        },
        activeActions() {
            if (this.exclude.length === 0) {
                return this.actionSchema;
            }

            const includedActions = {};

            for (const key in this.actionSchema) {
                if (!this.exclude.includes(key)) {
                    includedActions[`${key}`] = this.actionSchema[`${key}`];
                }
            }

            return includedActions;
        },
        node() {
            try {
                return findActiveNode(this.editor) || null;
            } catch (error) {
                return null;
            }
        }
    },
    methods: {
        run(command, args = null) {
            if (this.disabled) {
                return;
            }

            if (!this._.isFunction(command)) {
                throw new Error('Command must be a function specified in the schema');
            }

            return command(this, args);
        },
        can(action) {
            try {
                return this.editor.can()[`${action}`]();
            } catch (error) {
                return true;
            }
        }
    }
};
</script>
